// THE client authority read — ASK-104 ship 4.
//
// One module per client reads authority (permission-model-spec §"exactly one claim-reading module
// per client", failure-mode 2/6). Nothing else in the runtime may parse a claim.
//
// History: `main.ts:141` read ONE shape — flat `claims.role` — while the platform has stored a
// UNION since ASK-046 and is now moving to bundles + `grantsEpoch` with a `shop_grants` table
// behind `GET /admin/me` (ASK-104 ships 1-3). The console shipped this same bug and fixed it in
// ASK-103 slice A ("a user granted admin the modern way was refused every admin surface in the UI
// while the API happily served them"). On the 3D client the same bug is SILENT and worse: role
// reads null → `_maybeLoadStudio` returns early → the merchant walks their own shop as a customer
// with nothing in the console and no error anywhere.
//
// Resolution order:
//   1. `GET /admin/me` — the authorization plane's own answer (identity + bundles + scoped grants
//      + `grantsEpoch`). PREFERRED whenever it answers. Absent (404/501) or unreachable is the
//      normal case today (ship 1 is not deployed yet) and is NOT an error.
//   2. the ID token claim, read as a union: nested bundles (`admin` / `merchant` / `author`,
//      including the post-ship-3 `merchant:{shopsRef:true}` + `grantsEpoch` shape) OR the legacy
//      flat `role` + `roles{}` mirror that ship 3 deletes.
//
// It NEVER DOWNGRADES: if the plane answers with nothing usable (404, 401, timeout, empty body)
// and the claim carries authority, the claim wins. That is the whole point of the ordering rule —
// the claim shape may change under us without the Studio silently failing to load.
//
// The client is a MIRROR, never enforcement (spec §"Client-side is a mirror, never enforcement"):
// every write is still gated server-side by `requireShopAdmin`. It does NOT err toward chrome on a
// shop the authority itself says is someone else's (ASK-142 F-142d, below: Studio mounted for a
// merchant on Vanessa's shop, its reads were refused, and the save banner told Shaun his sign-in had
// expired). Where the authority carries no shop list, the server still decides — through the first
// read (a 403 there demotes the session to customer view), never through a save banner.

export const ADMIN_ME_PATH = "/admin/me";
// Bounded: the authority read sits on the boot critical path (main.ts mounts the runtime after
// it), so a hanging/slow plane must degrade to the claim rather than stall the scene.
export const ADMIN_ME_TIMEOUT_MS = 2500;

// Bundle-name tolerance. GCG's wire names are not frozen yet (ship 1 defines `/admin/me`), and
// server-side the same authority has historically been spelled several ways (`shop_admin` in the
// legacy `roles{}` mirror, "operator" in prose). Map the known spellings onto the three bundles
// the spec names; keep anything unknown verbatim rather than dropping it.
const BUNDLE_ALIASES = {
	admin: "admin", platform_admin: "admin", "platform-admin": "admin", operator: "admin",
	merchant: "merchant", shop_admin: "merchant", "shop-admin": "merchant", shopadmin: "merchant",
	author: "author", creator: "author",
};

// Legacy role strings that are NOT bundles (they gate other things and must not be invented into
// authority): "customer", "provisioner", "godmode".
const ROLE_RANK = ["admin", "merchant", "author"];

function alias(name) {
	const k = String(name || "").trim().toLowerCase();
	if (!k) return null;
	return BUNDLE_ALIASES[k] || k;
}

function pushBundle(list, name) {
	const b = alias(name);
	if (b && !list.includes(b)) list.push(b);
}

function pushShop(list, id) {
	const s = typeof id === "string" ? id.trim()
		: (id && typeof id === "object") ? String(id.shopId || id.shop || id.id || id.slug || "").trim()
		: "";
	if (s && !list.includes(s)) list.push(s);
}

/** Empty (signed-out / no authority) result — the shape every parser returns. */
export function emptyAuthority() {
	return { role: null, bundles: [], shops: [], shopsRef: false, grantsEpoch: null, approved: false, source: "none" };
}

/** Bundle set → the legacy role string the Studio gates still speak. Highest authority wins. */
export function roleFromBundles(bundles) {
	const list = Array.isArray(bundles) ? bundles : [];
	for (const r of ROLE_RANK) if (list.includes(r)) return r;
	return null;
}

/** Bundles off the wire: ["admin"] | [{name|bundle|id}] | {admin:true, merchant:false}. */
export function normaliseBundles(raw) {
	const out = [];
	if (Array.isArray(raw)) {
		for (const b of raw) pushBundle(out, typeof b === "string" ? b : (b && (b.name || b.bundle || b.id)));
	} else if (raw && typeof raw === "object") {
		for (const [k, v] of Object.entries(raw)) if (v) pushBundle(out, k);
	} else if (typeof raw === "string") {
		pushBundle(out, raw);
	}
	return out;
}

/**
 * Pure: a Firebase claims object → authority. Reads the union, tolerating every shape the platform
 * has minted — today's nested bundles, the post-ship-3 bundles + `grantsEpoch` claim, and the
 * legacy flat `role` / `roles{}` mirror ship 3 deletes.
 *
 * GCG nests role-scoping one level down (`{role, approved, claims:{merchant:{shops}}}`), which is
 * exactly what `studio-v1/_claims.js` learned the hard way — so read both levels.
 */
export function parseClaimAuthority(claims) {
	const c = (claims && typeof claims === "object") ? claims : {};
	const scoped = (c.claims && typeof c.claims === "object") ? c.claims : c;
	const pick = (k) => (scoped[k] !== undefined ? scoped[k] : c[k]);

	const bundles = [];
	const shops = [];
	let shopsRef = false;
	let perms = [];

	// admin: true (full bundle) | {perms:[...]} (staff subset, ASK-103 amendment shape)
	const admin = pick("admin");
	if (admin === true) pushBundle(bundles, "admin");
	else if (admin && typeof admin === "object") {
		pushBundle(bundles, "admin");
		if (Array.isArray(admin.perms)) perms = admin.perms.filter((p) => typeof p === "string");
	}

	// merchant: {shops:[...]} (today) | {shopsRef:true} (ship 3 — grants live in shop_grants)
	const merchant = pick("merchant");
	let merchantId = null;
	if (merchant && typeof merchant === "object") {
		pushBundle(bundles, "merchant");
		if (Array.isArray(merchant.shops)) for (const s of merchant.shops) pushShop(shops, s);
		if (merchant.shopsRef === true) shopsRef = true;
		if (typeof merchant.merchantId === "string") merchantId = merchant.merchantId;
	}
	if (!merchantId && typeof pick("merchantId") === "string") merchantId = pick("merchantId");

	// author: {tier:"1p"|"3p"}
	const author = pick("author");
	const authorTier = (author && typeof author === "object" && typeof author.tier === "string") ? author.tier : null;
	if (author) pushBundle(bundles, "author");

	// Bundle names may also arrive flat once the claim becomes bundles + epoch.
	for (const b of normaliseBundles(pick("bundles"))) pushBundle(bundles, b);

	// Legacy flat mirror — `role` is a display hint (firebaseAdmin.ts:68) and `roles{}` maps
	// shop → "shop_admin". Both are deleted by ship 3; both must still be honoured until then.
	const flatRole = typeof pick("role") === "string" ? pick("role") : null;
	if (flatRole && BUNDLE_ALIASES[flatRole.toLowerCase()]) pushBundle(bundles, flatRole);
	const rolesMap = pick("roles");
	if (rolesMap && typeof rolesMap === "object" && !Array.isArray(rolesMap)) {
		for (const [shopId, r] of Object.entries(rolesMap)) {
			if (typeof r !== "string") continue;
			pushBundle(bundles, r);
			if (alias(r) === "merchant") pushShop(shops, shopId);
		}
	}

	const epochRaw = pick("grantsEpoch") !== undefined ? pick("grantsEpoch") : pick("grants_epoch");
	const grantsEpoch = Number.isFinite(epochRaw) ? epochRaw : null;

	return {
		role: roleFromBundles(bundles) || flatRole || null,
		bundles, shops, shopsRef, grantsEpoch,
		perms, authorTier, merchantId,
		approved: c.approved === true || scoped.approved === true,
		source: bundles.length || flatRole ? "claim" : "none",
	};
}

/**
 * Pure: a `GET /admin/me` body → authority. The endpoint is specced but not yet deployed
 * (ASK-104 ship 1), so this tolerates the plausible spellings of the same three facts rather than
 * hard-coding one guess: bundle names, scoped grants, and `grantsEpoch`.
 */
export function parseAdminMe(body) {
	const b = (body && typeof body === "object") ? body : null;
	if (!b) return emptyAuthority();

	const bundles = normaliseBundles(b.bundles !== undefined ? b.bundles : (b.roles !== undefined ? b.roles : b.bundleNames));
	const shops = [];

	// Scoped grants: [{bundle, scopeType, scopeId}] under any of three names, with either
	// camelCase or snake_case keys, and a scope that may arrive pre-joined as "shop:kapow".
	const grants = [b.grants, b.scopedGrants, b.shopGrants, b.shop_grants].find(Array.isArray) || [];
	for (const g of grants) {
		if (!g || typeof g !== "object") continue;
		if (g.revokedAt || g.revoked_at) continue;
		pushBundle(bundles, g.bundle || g.name || g.role);
		const scopeType = String(g.scopeType || g.scope_type || g.scope || "").toLowerCase();
		const scopeId = g.scopeId || g.scope_id || g.shopId || g.shop_id || g.shop
			|| (scopeType.startsWith("shop:") ? scopeType.slice(5) : null);
		if (scopeType.startsWith("shop") && scopeId) pushShop(shops, scopeId);
	}

	// Convenience list some shapes carry alongside (or instead of) the grant rows.
	const flatShops = [b.shops, b.shopIds, b.shop_ids].find(Array.isArray) || [];
	for (const s of flatShops) pushShop(shops, s);
	// ASK-159 §2.18is (Client 5e) — a shop CONTEXT names both ids (GCG's canonical `id` and the runtime's `runtimeShopId`); the
	// runtime scopes by its own id, and GCG's gate accepts either, so both count. A platform/creator context is not a shop grant.
	for (const c of Array.isArray(b.contexts) ? b.contexts : []) {
		if (!c || typeof c !== "object" || c.kind !== "shop" || c.orphaned === true) continue;
		if (typeof c.id === "string" && c.id) pushShop(shops, c.id);
		if (typeof c.runtimeShopId === "string" && c.runtimeShopId) pushShop(shops, c.runtimeShopId);
	}

	// A shop-scoped grant IS merchant authority regardless of what the bundle row was named —
	// this is the tolerance that keeps the Studio loading if GCG spells the bundle differently.
	if (shops.length) pushBundle(bundles, "merchant");

	const epochRaw = b.grantsEpoch !== undefined ? b.grantsEpoch : (b.grants_epoch !== undefined ? b.grants_epoch : b.epoch);
	const flatRole = typeof b.role === "string" ? b.role : null;

	return {
		role: roleFromBundles(bundles) || flatRole || null,
		bundles, shops,
		shopsRef: b.shopsRef === true,
		grantsEpoch: Number.isFinite(epochRaw) ? epochRaw : null,
		perms: Array.isArray(b.perms) ? b.perms.filter((p) => typeof p === "string") : [],
		authorTier: (b.author && typeof b.author === "object" && typeof b.author.tier === "string") ? b.author.tier : null,
		// Ownership/billing binding, not a permission (spec §"Delegation is first-class") — carried
		// here only so the Studio keeps a merchantId when the claim stops carrying one.
		merchantId: typeof b.merchantId === "string" ? b.merchantId
			: typeof b.merchant_id === "string" ? b.merchant_id
			: (b.merchant && typeof b.merchant === "object" && typeof b.merchant.merchantId === "string") ? b.merchant.merchantId
			: null,
		approved: typeof b.approved === "boolean" ? b.approved : null,
		uid: typeof b.uid === "string" ? b.uid : null,
		email: typeof b.email === "string" ? b.email : null,
		source: bundles.length || flatRole ? "admin-me" : "none",
	};
}

// Fresh bearer token, same rule as the admin-write transports (`studio/_admin-fetch.js`): prefer
// the live provider (SDK-cached, transparently refreshed), fall back to the mount snapshot so
// probes that inject a token without a signed-in user keep working. Inlined rather than imported
// because this module must stay import-free — it is loaded by the CUSTOMER entry (bundle-budget
// leak guard) and is unit-tested in plain node.
async function authHeader(gw) {
	let tok = null;
	if (typeof gw.getFreshIdToken === "function") tok = await gw.getFreshIdToken().catch(() => null);
	tok = tok || gw.firebaseIdToken || null;
	return tok ? { Authorization: `Bearer ${tok}` } : {};
}

/**
 * GET /admin/me — one request, no preflight, bounded by `timeoutMs`, never throws.
 * Returns `{status, body}`; status 0 = not attempted / unreachable / timed out.
 */
export async function fetchAdminMe(gatewayConfig, { fetchImpl, timeoutMs = ADMIN_ME_TIMEOUT_MS } = {}) {
	const gw = (gatewayConfig && typeof gatewayConfig === "object") ? gatewayConfig : null;
	const base = gw && typeof gw.baseUrl === "string" ? gw.baseUrl.replace(/\/$/, "") : "";
	const doFetch = fetchImpl || (typeof fetch === "function" ? fetch.bind(globalThis) : null);
	if (!base || !doFetch) return { status: 0, body: null };

	const headers = await authHeader(gw);
	// Anonymous customer: there is no identity for the plane to resolve, so don't spend the
	// round-trip (and don't hand the gateway an unauthenticated /admin/* hit).
	if (!headers.Authorization) return { status: 0, body: null };

	const ctl = typeof AbortController === "function" ? new AbortController() : null;
	const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
	try {
		const r = await doFetch(`${base}${ADMIN_ME_PATH}`, {
			headers,
			cache: "no-store",
			...(ctl ? { signal: ctl.signal } : {}),
		});
		const body = r && r.ok ? await r.json().catch(() => null) : null;
		return { status: (r && r.status) || 0, body };
	} catch {
		// 404 today, network/abort tomorrow — all the same to the caller: fall back to the claim.
		return { status: 0, body: null };
	} finally {
		if (timer) clearTimeout(timer);
	}
}

/**
 * Resolve the signed-in identity's authority: `/admin/me` when it answers, the claim union
 * otherwise, and NEVER a downgrade of one by the other.
 */
export async function resolveAuthority({ gatewayConfig, claims, fetchImpl, timeoutMs } = {}) {
	const fromClaim = parseClaimAuthority(claims);
	const { status, body } = await fetchAdminMe(gatewayConfig, { fetchImpl, timeoutMs });
	const fromPlane = status === 200 ? parseAdminMe(body) : null;

	if (fromPlane && (fromPlane.role || fromPlane.bundles.length)) {
		return {
			...fromPlane,
			// The plane is authoritative, but an answer that carries no scoped grants must not
			// erase shops the claim still knows about (ship 1/2 run claim-first server-side too).
			shops: fromPlane.shops.length ? fromPlane.shops : fromClaim.shops,
			shopsRef: fromPlane.shopsRef || fromClaim.shopsRef,
			merchantId: fromPlane.merchantId || fromClaim.merchantId || null,
			approved: typeof fromPlane.approved === "boolean" ? fromPlane.approved : fromClaim.approved,
			source: "admin-me",
			adminMeStatus: status,
			claimRole: fromClaim.role,
		};
	}

	return { ...fromClaim, adminMeStatus: status, claimRole: fromClaim.role };
}

/** One-line console summary — the silent-failure antidote: authority is always stated at boot. */
export function describeAuthority(a) {
	const x = a || emptyAuthority();
	const parts = [
		`role=${x.role || "none"}`,
		`bundles=[${(x.bundles || []).join(",") || "-"}]`,
		`shops=${(x.shops || []).length}${x.shopsRef ? "+ref" : ""}`,
		`epoch=${x.grantsEpoch === null || x.grantsEpoch === undefined ? "-" : x.grantsEpoch}`,
		`source=${x.source}`,
	];
	if (x.adminMeStatus !== undefined && x.source !== "admin-me") parts.push(`admin-me=${x.adminMeStatus || "absent"}`);
	return parts.join(" ");
}

/**
 * ASK-142 F-142d — may this authority mount the Studio on THIS shop? Pure; `test-authority-read` pins it.
 *   • not a shop (the hub, nothing loaded): unchanged — the role alone decides;
 *   • admin (an operator): ONLY a shop on its grant list (ASK-159 §2.18is, Client 5e — GCG's per-shop gate never lets a
 *     platform admin through: adminAuth.ts requireShopAdmin), or `shopsRef` (grants server-side); otherwise the operator
 *     view + "not your shop" (`operatorForeignShop`) and no shop-scoped request at all;
 *   • merchant WITH a shop list (the claim's `merchant.shops` / legacy `roles{shop: shop_admin}`, or
 *     `/admin/me`'s grants): only a shop on that list;
 *   • merchant with NO list (`shopsRef:true` — grants live server-side — or a bare role): yes, and the
 *     first load-time read decides (a 403 there demotes to customer view, `studio/index.js`);
 *   • anything else: no.
 * @param {object} authority `config.authority` (`resolveAuthority`)
 * @param {{ shopId?: string|null, isShop?: boolean }} active
 */
export function mayAuthorShop(authority, { shopId = null, isShop = true } = {}) {
	const a = authority || {};
	const bundles = Array.isArray(a.bundles) ? a.bundles : [];
	const role = a.role || roleFromBundles(bundles);
	if (role !== "admin" && role !== "merchant" && !bundles.includes("admin") && !bundles.includes("merchant")) return false;
	if (!isShop || !shopId) return true;
	const shops = Array.isArray(a.shops) ? a.shops : [];
	if (role === "admin" || bundles.includes("admin")) return shops.includes(shopId) || a.shopsRef === true;
	return shops.length ? shops.includes(shopId) : true;
}

/** ASK-159 §2.18is (Client 5e) — an operator (admin) standing in a shop it does not administer: the Studio shows the
 *  operator view + a quiet "not your shop" state instead of merchant panels. Pure; `test-authority-read` pins it. */
export function operatorForeignShop(authority, { shopId = null, isShop = true } = {}) {
	const a = authority || {};
	const bundles = Array.isArray(a.bundles) ? a.bundles : [];
	const role = a.role || roleFromBundles(bundles);
	if (!isShop || !shopId || !(role === "admin" || bundles.includes("admin"))) return false;
	return !mayAuthorShop(a, { shopId, isShop });
}
